/**
 * Balance Statement Component
 * Generate and download balance statements for specific periods
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBalance } from '../../hooks/useBalance';
import { useI18n } from '../../i18n/i18nContext';
import type { BalanceStatement as BalanceStatementType } from '../../types/balance';
import './BalanceStatement.css';

interface BalanceStatementProps {
    userId: number;
    userRole: 'customer' | 'driver' | 'admin';
}

const BalanceStatement: React.FC<BalanceStatementProps> = ({ userId, userRole }) => {
    const navigate = useNavigate();
    const { t, language } = useI18n();
    const { statement, loading, error, generateStatement } = useBalance();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState<string>('');
    const [validationError, setValidationError] = useState('');

    const presetPeriods = [
        { id: 'last7days', label: t('balanceStatement.periodLast7Days'), days: 7 },
        { id: 'last30days', label: t('balanceStatement.periodLast30Days'), days: 30 },
        { id: 'last3months', label: t('balanceStatement.periodLast3Months'), days: 90 },
        { id: 'last6months', label: t('balanceStatement.periodLast6Months'), days: 180 },
        { id: 'lastyear', label: t('balanceStatement.periodLastYear'), days: 365 },
        { id: 'custom', label: t('balanceStatement.periodCustom'), days: 0 }
    ];

    const handlePeriodSelect = (periodId: string) => {
        setSelectedPeriod(periodId);
        setValidationError('');

        if (periodId === 'custom') {
            setStartDate('');
            setEndDate('');
            return;
        }

        const period = presetPeriods.find(p => p.id === periodId);
        if (period && period.days > 0) {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - period.days);

            setEndDate(end.toISOString().split('T')[0]);
            setStartDate(start.toISOString().split('T')[0]);
        }
    };

    const validateDates = (): boolean => {
        if (!startDate || !endDate) {
            setValidationError(t('balanceStatement.errorMissingDates'));
            return false;
        }

        const start = new Date(startDate);
        const end = new Date(endDate);
        const now = new Date();

        if (start > now) {
            setValidationError(t('balanceStatement.errorFutureStart'));
            return false;
        }

        if (end > now) {
            setValidationError(t('balanceStatement.errorFutureEnd'));
            return false;
        }

        if (start > end) {
            setValidationError(t('balanceStatement.errorEndBeforeStart'));
            return false;
        }

        const daysDiff = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff > 365) {
            setValidationError(t('balanceStatement.errorMaxPeriod'));
            return false;
        }

        setValidationError('');
        return true;
    };

    const handleGenerateStatement = async () => {
        if (!validateDates()) return;

        await generateStatement(userId, startDate, endDate);
    };

    const handleDownloadPDF = () => {
        if (!statement) return;
        // In production, this would call a PDF generation service
        alert('PDF download would be triggered here');
    };

    const handleDownloadCSV = () => {
        if (!statement) return;

        const headers = ['Date', 'Type', 'Description', 'Amount', 'Balance After'];
        const rows = statement.transactions.map(t => [
            new Date(t.createdAt).toLocaleDateString(),
            t.type,
            t.description,
            t.amount.toFixed(2),
            t.balanceAfter.toFixed(2)
        ]);

        const summary = [
            [''],
            ['SUMMARY'],
            ['Opening Balance', statement.openingBalance.toFixed(2)],
            ['Total Deposits', statement.totalDeposits.toFixed(2)],
            ['Total Withdrawals', statement.totalWithdrawals.toFixed(2)],
            ['Total Earnings', statement.totalEarnings.toFixed(2)],
            ['Total Deductions', statement.totalDeductions.toFixed(2)],
            ['Closing Balance', statement.closingBalance.toFixed(2)]
        ];

        const csvContent = [
            ...summary.map(row => row.join(',')),
            [''],
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `balance-statement-${startDate}-to-${endDate}.csv`;
        a.click();
    };

    const formatCurrency = (amount: number, currency: string = 'EGP') => {
        return `${amount.toFixed(2)} ${currency}`;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="balance-statement" data-testid="balance-statement">
            <div className="statement-header" data-testid="statement-header">
                <button onClick={() => navigate(-1)} className="back-btn matrix-btn-ghost">
                    ← {t('balanceStatement.back') || 'Back'}
                </button>
                <h1 data-testid="statement-title">{t('balanceStatement.title')}</h1>
            </div>

            <div className="statement-generator" data-testid="statement-generator">
                <h2>{t('balanceStatement.generateTitle')}</h2>

                <div className="period-selector" data-testid="period-selector">
                    <label>{t('balanceStatement.selectPeriod')}</label>
                    <div className="period-options" data-testid="period-options">
                        {presetPeriods.map((period) => (
                            <button
                                key={period.id}
                                className={`period-btn ${selectedPeriod === period.id ? 'active' : ''}`}
                                onClick={() => handlePeriodSelect(period.id)}
                                data-testid={`period-${period.id}`}
                            >
                                {period.label}
                            </button>
                        ))}
                    </div>
                </div>

                {(selectedPeriod === 'custom' || selectedPeriod) && (
                    <div className="date-range-selector" data-testid="date-range-selector">
                        <div className="date-input-group">
                            <label htmlFor="start-date">{t('balanceStatement.startDate')}</label>
                            <input
                                id="start-date"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                                data-testid="start-date-input"
                            />
                        </div>

                        <div className="date-input-group">
                            <label htmlFor="end-date">{t('balanceStatement.endDate')}</label>
                            <input
                                id="end-date"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                                data-testid="end-date-input"
                            />
                        </div>
                    </div>
                )}

                {validationError && (
                    <div className="validation-error" data-testid="validation-error">
                        <span className="error-icon">⚠️</span>
                        {validationError}
                    </div>
                )}

                {error && (
                    <div className="error-message" data-testid="error-message">
                        <span className="error-icon">⚠️</span>
                        {error}
                    </div>
                )}

                <button
                    className="generate-btn"
                    onClick={handleGenerateStatement}
                    disabled={!startDate || !endDate || loading}
                    data-testid="generate-statement-button"
                >
                    {loading ? t('balanceStatement.generating') : t('balanceStatement.generate')}
                </button>
            </div>

            {statement && (
                <div className="statement-preview" data-testid="statement-preview">
                    <div className="preview-header" data-testid="preview-header">
                        <h2>{t('balanceStatement.previewTitle')}</h2>
                        <div className="download-buttons" data-testid="download-buttons">
                            <button className="download-btn pdf" onClick={handleDownloadPDF} data-testid="download-pdf-button">
                                <span className="btn-icon">📄</span>
                                {t('balanceStatement.downloadPDF')}
                            </button>
                            <button className="download-btn csv" onClick={handleDownloadCSV} data-testid="download-csv-button">
                                <span className="btn-icon">📊</span>
                                {t('balanceStatement.downloadCSV')}
                            </button>
                        </div>
                    </div>

                    <div className="statement-content" data-testid="statement-content">
                        <div className="statement-info" data-testid="statement-info">
                            <h3>{t('balanceStatement.statementType')}</h3>
                            <p className="period" data-testid="statement-period">
                                {formatDate(statement.period.startDate)} - {formatDate(statement.period.endDate)}
                            </p>
                        </div>

                        <div className="statement-summary" data-testid="statement-summary">
                            <div className="summary-grid">
                                <div className="summary-item" data-testid="opening-balance">
                                    <span className="label">{t('balanceStatement.openingBalance')}</span>
                                    <span className="value">
                                        {formatCurrency(statement.openingBalance, statement.currency)}
                                    </span>
                                </div>

                                <div className="summary-item positive" data-testid="total-deposits">
                                    <span className="label">{t('balanceStatement.totalDeposits')}</span>
                                    <span className="value">
                                        +{formatCurrency(statement.totalDeposits, statement.currency)}
                                    </span>
                                </div>

                                <div className="summary-item negative" data-testid="total-withdrawals">
                                    <span className="label">{t('balanceStatement.totalWithdrawals')}</span>
                                    <span className="value">
                                        -{formatCurrency(statement.totalWithdrawals, statement.currency)}
                                    </span>
                                </div>

                                {userRole === 'driver' && (
                                    <>
                                        <div className="summary-item positive" data-testid="total-earnings">
                                            <span className="label">{t('balanceStatement.totalEarnings')}</span>
                                            <span className="value">
                                                +{formatCurrency(statement.totalEarnings, statement.currency)}
                                            </span>
                                        </div>

                                        <div className="summary-item negative" data-testid="total-deductions">
                                            <span className="label">{t('balanceStatement.totalDeductions')}</span>
                                            <span className="value">
                                                -{formatCurrency(statement.totalDeductions, statement.currency)}
                                            </span>
                                        </div>
                                    </>
                                )}

                                <div className="summary-item total" data-testid="closing-balance">
                                    <span className="label">{t('balanceStatement.closingBalance')}</span>
                                    <span className="value">
                                        {formatCurrency(statement.closingBalance, statement.currency)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="statement-transactions" data-testid="statement-transactions">
                            <h4 data-testid="transactions-count">{t('balanceStatement.transactionDetails').replace('{count}', statement.transactions.length.toString())}</h4>
                            <div className="transactions-list" data-testid="transactions-list">
                                {statement.transactions.map((transaction, index) => (
                                    <div key={index} className="transaction-row" data-testid="statement-transaction-row">
                                        <div className="transaction-date">
                                            {new Date(transaction.createdAt).toLocaleDateString()}
                                        </div>
                                        <div className="transaction-type">
                                            {transaction.type.replace(/_/g, ' ')}
                                        </div>
                                        <div className="transaction-description">
                                            {transaction.description}
                                        </div>
                                        <div className={`transaction-amount ${transaction.amount >= 0 ? 'positive' : 'negative'}`}>
                                            {transaction.amount >= 0 ? '+' : ''}
                                            {formatCurrency(Math.abs(transaction.amount), transaction.currency)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BalanceStatement;
